import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase/firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { getUserProfile, getUserById } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // Function to fetch user profile from database using authenticated endpoint
    const fetchUserProfile = async (firebaseUser, retryCount = 0) => {
        if (!firebaseUser) {
            setUserProfile(null);
            return;
        }

        try {
            // Get Firebase token and use authenticated endpoint to ensure user is created in DB
            const token = await firebaseUser.getIdToken();
            const profile = await getUserProfile(token);
            setUserProfile(profile);
        } catch (error) {
            console.error('Error fetching user profile:', error);
            
            // If this is a "USER_NOT_REGISTERED" error and we haven't retried too many times,
            // wait a bit and retry (user might be in the middle of registration)
            if (error.code === 'USER_NOT_REGISTERED' && retryCount < 3) {
                setTimeout(() => {
                    fetchUserProfile(firebaseUser, retryCount + 1);
                }, 1000);
                return;
            }
            
            // If still failing after retries or different error, set to null
            setUserProfile(null);
        }
    };

    // Function to refresh user profile (call this after profile updates)
    const refreshUserProfile = async () => {
        if (user) {
            await fetchUserProfile(user);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                await fetchUserProfile(currentUser);
            } else {
                setUserProfile(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const logout = async () => {
        try {
            await signOut(auth);
            setUserProfile(null);
        } catch (error) {
            console.error('Error signing out:', error);
            throw error;
        }
    };

    // Create combined user object with both Firebase and database data
    const combinedUser = user ? {
        ...user,
        profile: userProfile,
        displayName: userProfile?.display_name || userProfile?.username || user.displayName || user.email?.split('@')[0],
        // Preserve Firebase user methods
        getIdToken: user.getIdToken.bind(user),
        reload: user.reload ? user.reload.bind(user) : undefined,
        delete: user.delete ? user.delete.bind(user) : undefined
    } : null;

    return (
        <AuthContext.Provider value={{ 
            user: combinedUser, 
            userProfile, 
            loading, 
            logout, 
            refreshUserProfile 
        }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);